import { SquareFunction } from 'lucide-react';
import { HoverCard as HoverCardPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from './lib/utils';

/**
 * THE formula glyph. Every "how is this calculated?" in the product draws this
 * one — the hint's trigger, its panel, a report footer's definitions toggle —
 * so a reader learns one mark rather than an ƒ letter here and an icon there.
 * `size` in pixels, not a class, so hosts without Tailwind (report-kit's own
 * CSS) can use it too.
 */
export function FormulaIcon({ size = 14, className }: { size?: number; className?: string }) {
  return <SquareFunction size={size} className={className} aria-hidden="true" />;
}

const HOVER_DELAY_MS = 150;

/** True below a `FormulaHintProvider` — see it for why the hints share one. */
const SharedProvider = React.createContext(false);

/**
 * ONE tooltip provider for every formula hint beneath it.
 *
 * A provider per hint meant Radix's skip-delay never applied: it is the
 * PROVIDER that remembers a tooltip just closed, so moving from one ƒ to the
 * next opens instantly only when both share one. A picker of forty measures or
 * a pivot of twelve column headers paid the full delay on every one, and
 * mounted a provider per row to do it.
 *
 * Mount it around a surface that holds many hints (the report builder, a report
 * view, a dashboard). Nesting is a no-op, so a surface can mount it without
 * knowing whether its host already did. Deliberately NOT mounted at an app's
 * root: Radix tooltips take their timing from the NEAREST provider, so a root
 * mount would retime every other tooltip in the app.
 */
export function FormulaHintProvider({ children }: { children: React.ReactNode }) {
  const inside = React.useContext(SharedProvider);
  if (inside) return children;
  return (
    <SharedProvider.Provider value={true}>
      <TooltipPrimitive.Provider delayDuration={HOVER_DELAY_MS} skipDelayDuration={300}>
        {children}
      </TooltipPrimitive.Provider>
    </SharedProvider.Provider>
  );
}

/**
 * A measure's formula, behind an ƒ icon.
 *
 * The "how is this number calculated?" icon wherever one is wanted — the
 * report builder's measure picker and shelves, a dashboard metric tile. NOT a
 * report's column headers: those show it on demand from the label itself
 * (`DefinitionHoverCard`), with no glyph. Printed in full, a paragraph of
 * `SUM of … WHERE … AND …` under every name read as noise to anyone who just
 * wanted "Billable Hours"; a native `title` was slow, unstyled and unreachable
 * on touch. This is one hover away, and a click (or a tap) pins it open until
 * the reader clicks elsewhere or presses Escape.
 *
 * Hover and pin are separate state on purpose. Radix reports a hover-leave and
 * an outside click through the same `onOpenChange(false)`, so a single `open`
 * flag could not keep a pinned panel up while the pointer moved away to read
 * the rest of the page.
 *
 * `focusable` decides the trigger element. Wherever it can be, it is a real
 * button, so the formula is reachable from the keyboard (and the shelf's drag
 * guard already treats a press on a button as a press). Inside a picker row it
 * must NOT be: the row is itself a `<button role="option">`, and a button
 * nested in a button is invalid HTML that browsers re-parent — so there it is a
 * plain span, and the row carries the formula as its `aria-description`.
 *
 * Shares the nearest `FormulaHintProvider`, so moving between hints skips the
 * delay; mounts one of its own only when there is none above it, so it still
 * renders in a host that never did (Radix's Tooltip throws without one).
 */
export function FormulaHint({
  formula,
  label,
  focusable = false,
  align = 'center',
  className,
  contentClassName,
  children,
}: {
  /** The formula text. Ignored when `children` supplies richer content. */
  formula?: string;
  /** The measure's name, for the trigger's accessible name. */
  label?: string;
  focusable?: boolean;
  align?: 'start' | 'center' | 'end';
  className?: string;
  contentClassName?: string;
  /** Replaces the default `ƒ <formula>` body — a tile shows the whole definition. */
  children?: React.ReactNode;
}) {
  const [hovered, setHovered] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);

  const triggerClass = cn(
    'inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground',
    'hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
    pinned && 'text-foreground',
    className,
  );
  const triggerProps = {
    className: triggerClass,
    'data-testid': 'formula-hint',
    onClick: (e: React.MouseEvent) => {
      // Not a click on what it sits in: inside a picker row this would
      // otherwise toggle the measure. And default-prevented so Radix skips the
      // close it chains onto every trigger click.
      e.stopPropagation();
      e.preventDefault();
      setPinned((p) => !p);
    },
  };
  const icon = <FormulaIcon />;

  return (
    <FormulaHintProvider>
      {/* The delay on the ROOT too, so a host's own Radix provider mounted
          between ours and this hint cannot retime it. */}
      <TooltipPrimitive.Root
        open={hovered || pinned}
        onOpenChange={setHovered}
        delayDuration={HOVER_DELAY_MS}
      >
        <TooltipPrimitive.Trigger asChild>
          {focusable ? (
            <button
              type="button"
              {...triggerProps}
              ref={triggerRef as React.Ref<HTMLButtonElement>}
              aria-label={label ? `How ${label} is calculated` : 'How this is calculated'}
              aria-expanded={pinned}
            >
              {icon}
            </button>
          ) : (
            <span
              {...triggerProps}
              ref={triggerRef as React.Ref<HTMLSpanElement>}
              aria-hidden="true"
            >
              {icon}
            </span>
          )}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            align={align}
            sideOffset={4}
            collisionPadding={8}
            onPointerDownOutside={(e) => {
              // A press on the icon is the click that toggles the pin — let
              // that decide, rather than dismissing here and re-pinning there.
              if (triggerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
                return;
              }
              setPinned(false);
            }}
            onEscapeKeyDown={() => setPinned(false)}
            className={cn(
              'z-50 max-w-sm rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md',
              contentClassName,
            )}
          >
            {children ?? (
              <span className="flex items-start gap-1.5">
                <FormulaIcon size={13} className="mt-px shrink-0 text-muted-foreground" />
                <span>{formula}</span>
              </span>
            )}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </FormulaHintProvider>
  );
}

/**
 * A measure's description with its formula underneath — ON DEMAND from
 * something already on screen, with no icon of its own.
 *
 * For a report's column headers. An ƒ on every header was a row of glyphs a
 * reader never asked for, so the header's own label is the trigger: pointing
 * at it (or focusing its sort button) opens a small card with the
 * plain-language description, and the formula under it. One hover, both
 * answers — "what is this" and "how is it worked out".
 *
 * A HoverCard rather than a Tooltip so the pointer can travel INTO the card to
 * read or select a long formula without it closing. No pin — a click on a
 * header sorts.
 *
 * `children` must be ONE element that takes a ref and DOM props (a span
 * around the label is the usual one). Renders it bare when there is nothing
 * to tell.
 */
export function DefinitionHoverCard({
  description,
  formula,
  children,
}: {
  description?: string;
  formula?: string;
  children: React.ReactElement;
}) {
  if (!description && !formula) return children;
  return (
    <HoverCardPrimitive.Root openDelay={HOVER_DELAY_MS} closeDelay={200}>
      <HoverCardPrimitive.Trigger asChild>{children}</HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="top"
          sideOffset={6}
          collisionPadding={8}
          data-testid="measure-definition"
          className="z-50 grid max-w-sm gap-1.5 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
        >
          {description ? <p>{description}</p> : null}
          {formula ? (
            <p className="flex items-start gap-1.5 text-muted-foreground">
              <FormulaIcon size={13} className="mt-px shrink-0" />
              <span>{formula}</span>
            </p>
          ) : null}
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
